import { zodResolver } from "@hookform/resolvers/zod";
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useServerFn } from "@tanstack/react-start";
import { ChevronDown } from "lucide-react";
import { ChevronUp } from "lucide-react";
import { Search } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Controller } from "react-hook-form";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { CoachCard } from "~/components/coach-card";
import type { CoachCardMinimalData } from "~/components/coach-card";
import { CoachCardSkeleton } from "~/components/coach-card-skeleton";
import { CountrySelect } from "~/components/country-select";
import { CheckboxGroupField } from "~/components/form/checkbox-group-field";
import { ControlledRadioGroup } from "~/components/form/controlled-inputs";
import { LanguageSelect } from "~/components/language-select";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { CustomMultiSelect } from "~/components/ui/custom-multi-select";
import { EmptyState } from "~/components/ui/empty-state";
import { FormField } from "~/components/ui/form-field";
import { Input } from "~/components/ui/input";
import { PageTitle } from "~/components/ui/page-title";
import { RadioGroup } from "~/components/ui/radio-group";
import { RadioGroupItem } from "~/components/ui/radio-group";
import { Textarea } from "~/components/ui/textarea";
import {
  ValidationMessage,
  parseServerError,
  formErrorsToFieldErrors,
  type ParsedError,
} from "~/components/ui/validation-message";
import ServerErrorGraphicUrl from "~/img/server-error.svg?url";
import { authMiddleware } from "~/lib/auth-middleware";
import { cn } from "~/lib/cn";
import { COACH_STATUS_COPY } from "~/lib/coach-status";
import {
  CoachFinderSchema,
  coachAgesField,
  coachGenderField,
  coachLocationCountryField,
  coachLocationRegionField,
  coachPersonalityField,
  coachingApproachesField,
  coachingFormatsFields,
  coachingPackagesField,
  faithIntegrationsField,
  goalsField,
  otherFaithIntegrationField,
  otherGoalField,
  personalContextField,
  prefersCoachLocationField,
  sessionDeliveryMethodsFeild,
  startTimeField,
  supportField,
  userPersonalityField,
} from "~/lib/schemas/coach-discovery";
import type {
  CoachDiscoveryField,
  CoachDiscoveryForm,
} from "~/lib/schemas/coach-discovery";
import type { CoachMatchPreferences } from "~/lib/server/coach-matching";
import { buildEmbeddingPrompt } from "~/lib/shared/embedding-prompts";
import { createNoIndexMeta } from "~/lib/shared/meta";
import { UserFacingError } from "~/lib/shared/user-facing-error";
import { transformUserPreferencesToDiscoveryForm } from "~/lib/transforms/user-preferences";

const coachStatusOptions = [
  {
    value: "founding",
    label: "Founding coaches only",
    tooltip: COACH_STATUS_COPY.founding.tooltip,
  },
] satisfies Array<{ value: string; label: string; tooltip: string }>;

export const getFormData = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }): Promise<CoachDiscoveryForm> => {
    const { getUserPreferences } = await import("~/lib/server/user");
    const userPreferences = await getUserPreferences(context.user.id);
    return transformUserPreferencesToDiscoveryForm(userPreferences);
  });

export const getCoaches = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator((data: unknown) => {
    const result = CoachFinderSchema.safeParse(data);
    if (!result.success) {
      throw new UserFacingError(JSON.stringify(result.error.issues));
    }
    return result.data;
  })
  .handler(
    async ({
      data: values,
      context: _context,
    }): Promise<CoachCardMinimalData[]> => {
      const embeddingPrompt = buildEmbeddingPrompt(values);

      const { getEmbeddingsClient } =
        await import("~/lib/server/embeddings-client");

      const embedding =
        await getEmbeddingsClient().createEmbedding(embeddingPrompt);

      const preferences: CoachMatchPreferences = {
        ...values,
        goals: values.goals || [],
        personalContext: values.personalContext || "",
        sessionDeliveryMethods:
          values.sessionDeliveryMethods || "No preference",
        prefersCoachLocation: values.prefersCoachLocation || "No preference",
        coachGender: values.coachGender || "No preference",
        coachingFormats: values.coachingFormats || "any",
        startTime: values.startTime || "I'm flexible",
        support: values.support || "Not sure yet – I'd like guidance on this",
        preferredLanguages: values.preferredLanguages || [],
        faithIntegration: values.faithIntegration || [],
        coachAges: values.coachAges || [],
        coachLocationRegion: values.coachLocationRegion || "No preference",
        coachLocationCountry: values.coachLocationCountry || "No preference",
        otherFaithIntegration: values.otherFaithIntegration || "No preference",
        coachPersonality: values.coachPersonality || [],
        userPersonality: values.userPersonality || [],
        coachingPackages: values.coachingPackages || [],
        coachingApproaches: values.coachingApproaches || [],
        embedding,
      };

      const { findCoachMatches, toCoachCardData } =
        await import("~/lib/server/coach-matching");
      const matches = await findCoachMatches({
        preferences,
        limit: 24,
        options: {
          statusFilters: values.statusFilters || [],
        },
      });
      return matches.map(toCoachCardData);
    },
  );

export const savePreferences = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator((data: unknown) => CoachFinderSchema.parse(data))
  .handler(async ({ data: values, context }) => {
    const embeddingPrompt = buildEmbeddingPrompt(values);

    const [{ getEmbeddingsClient }, { updateUserPreferences }] =
      await Promise.all([
        import("~/lib/server/embeddings-client"),
        import("~/lib/server/user"),
      ]);

    const embedding =
      await getEmbeddingsClient().createEmbedding(embeddingPrompt);

    await updateUserPreferences(context.user.id, {
      ...values,
      preferencesEmbedding: embedding,
    });

    return { status: "success" } as const;
  });

// Build a complete preferences payload with defaults to ensure stability
function buildCompletePreferences(
  data: Partial<CoachDiscoveryForm> | undefined,
): Required<
  Pick<
    CoachDiscoveryForm,
    | "goals"
    | "coachingFormats"
    | "sessionDeliveryMethods"
    | "prefersCoachLocation"
    | "coachGender"
    | "faithIntegration"
    | "preferredLanguages"
    | "coachAges"
    | "coachingPackages"
    | "coachingApproaches"
    | "coachPersonality"
  >
> &
  Partial<CoachDiscoveryForm> {
  const base = data ?? {};
  return {
    // include all other fields as-is to preserve user input
    ...base,
    goals: base.goals ?? [],
    coachingFormats: base.coachingFormats || "any",
    sessionDeliveryMethods: base.sessionDeliveryMethods || "No preference",
    prefersCoachLocation: base.prefersCoachLocation || "No preference",
    coachGender: base.coachGender || "No preference",
    faithIntegration: base.faithIntegration ?? [],
    preferredLanguages: base.preferredLanguages ?? [],
    coachAges: base.coachAges ?? [],
    coachingPackages: base.coachingPackages ?? [],
    coachingApproaches: base.coachingApproaches ?? [],
    coachPersonality: base.coachPersonality ?? [],
  };
}

const errorFieldLabels: Partial<Record<CoachDiscoveryField, string>> = {
  goals: "Goals",
  otherGoal: "Other goal description",
  startTime: "Desired start time",
  support: "Support level",
  coachingFormats: "Coaching format",
  sessionDeliveryMethods: "Session delivery method",
  prefersCoachLocation: "Coach location preference",
  coachLocationCountry: "Coach location country",
  coachLocationRegion: "Coach location region",
  preferredLanguages: "Preferred languages",
  coachGender: "Coach gender",
  coachAges: "Coach ages",
  coachingPackages: "Coaching experience levels",
  coachingApproaches: "Coaching approaches",
  coachPersonality: "Preferred coach personality",
  userPersonality: "Your personality",
  faithIntegration: "Faith preferences",
  otherFaithIntegration: "Other faith preference",
  personalContext: "Personal context",
};

const getErrorMessage = (error: unknown): string | undefined => {
  if (!error) {
    return undefined;
  }

  if (typeof error === "string") {
    return error;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }

  return undefined;
};

// Query options definitions
export const coachDiscoveryFormQuery = queryOptions({
  queryKey: ["coach-discovery-form"] as const,
  queryFn: (): Promise<CoachDiscoveryForm> => getFormData(),
  staleTime: 5 * 60 * 1000,
});

export const coachesQuery = (prefs: Partial<CoachDiscoveryForm>) =>
  queryOptions({
    queryKey: ["coaches", prefs] as const,
    queryFn: (): Promise<CoachCardMinimalData[]> =>
      getCoaches({ data: buildCompletePreferences(prefs) }),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      // Don't retry on validation errors (user errors, not transient failures)
      if (parseServerError(error).type === "validation") {
        return false;
      }
      return failureCount < 3;
    },
  });

const coachFinderSearchSchema = z.object({
  coachingFormats: z
    .enum(["1-on-1", "both", "group", "undecided", "any"])
    .optional(),
});

export const Route = createFileRoute("/_user/coach-finder/")({
  staticData: { title: "Coach Finder" },
  validateSearch: coachFinderSearchSchema,
  head: () =>
    createNoIndexMeta(
      "Find A Coach – Dream Coach Match",
      "Discover your perfect coach with our advanced matching system",
    ),
  loader: async ({ context }) => {
    const form: CoachDiscoveryForm = await context.queryClient.ensureQueryData(
      coachDiscoveryFormQuery,
    );

    const initialValidation = CoachFinderSchema.safeParse(form);

    if (initialValidation.success) {
      // Kick off the coaches query on the server for SSR streaming
      // Do not await: stream to client without blocking full SSR
      void context.queryClient.fetchQuery(coachesQuery(form));
    }

    return {};
  },
  component: CoachDiscoveryPage,
});

function CoachDiscoveryPage() {
  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(false);
  const queryClient = useQueryClient();

  const { data: initialFormData } = useSuspenseQuery(coachDiscoveryFormQuery);
  const savePreferencesFn = useServerFn(savePreferences);

  const { coachingFormats: presetFormat } = Route.useSearch();

  const {
    register,
    handleSubmit: _handleSubmit,
    watch,
    setValue,
    getValues,
    formState,
    control,
    trigger,
  } = useForm<CoachDiscoveryForm>({
    resolver: zodResolver(CoachFinderSchema),
    defaultValues: presetFormat
      ? { ...initialFormData, coachingFormats: presetFormat }
      : initialFormData,
  });

  const [isSavingPreferences, setIsSavingPreferences] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const savedFilters = localStorage.getItem("coachStatusFilters");
    if (savedFilters) {
      try {
        const statusFiltersSchema = z.array(z.enum(["founding"]));
        const parsed = statusFiltersSchema.parse(JSON.parse(savedFilters));
        setValue("statusFilters", parsed);
      } catch (error) {
        console.warn(
          "Failed to parse saved status filters from localStorage",
          error,
        );
        localStorage.removeItem("coachStatusFilters");
      }
    }
  }, [setValue]);

  // Watch all relevant form fields that affect coach matching
  const watchedFields = watch([
    "goals",
    "otherGoal",
    "startTime",
    "support",
    "coachingFormats",
    "sessionDeliveryMethods",
    "prefersCoachLocation",
    "coachLocationCountry",
    "coachLocationRegion",
    "preferredLanguages",
    "coachGender",
    "coachAges",
    "coachingPackages",
    "coachingApproaches",
    "coachPersonality",
    "userPersonality",
    "faithIntegration",
    "otherFaithIntegration",
    "personalContext",
    "statusFilters",
  ]);

  // Save statusFilters to localStorage whenever it changes
  const statusFilters = watch("statusFilters");
  useEffect(() => {
    if (typeof window === "undefined") return;

    if (statusFilters) {
      localStorage.setItem("coachStatusFilters", JSON.stringify(statusFilters));
    }
  }, [statusFilters]);

  // Memoize form values object to prevent unnecessary re-renders
  const formValues = useMemo(() => {
    const [
      goals,
      otherGoal,
      startTime,
      support,
      coachingFormats,
      sessionDeliveryMethods,
      prefersCoachLocation,
      coachLocationCountry,
      coachLocationRegion,
      preferredLanguages,
      coachGender,
      coachAges,
      coachingPackages,
      coachingApproaches,
      coachPersonality,
      userPersonality,
      faithIntegration,
      otherFaithIntegration,
      personalContext,
      statusFilters,
    ] = watchedFields;
    return {
      goals,
      otherGoal,
      startTime,
      support,
      coachingFormats,
      sessionDeliveryMethods,
      prefersCoachLocation,
      coachLocationCountry,
      coachLocationRegion,
      preferredLanguages,
      coachGender,
      coachAges,
      coachingPackages,
      coachingApproaches,
      coachPersonality,
      userPersonality,
      faithIntegration,
      otherFaithIntegration,
      personalContext,
      statusFilters,
    } as {
      goals: CoachDiscoveryForm["goals"];
      otherGoal: CoachDiscoveryForm["otherGoal"];
      startTime: CoachDiscoveryForm["startTime"];
      support: CoachDiscoveryForm["support"];
      coachingFormats: CoachDiscoveryForm["coachingFormats"];
      sessionDeliveryMethods: CoachDiscoveryForm["sessionDeliveryMethods"];
      prefersCoachLocation: CoachDiscoveryForm["prefersCoachLocation"];
      coachLocationCountry: CoachDiscoveryForm["coachLocationCountry"];
      coachLocationRegion: CoachDiscoveryForm["coachLocationRegion"];
      preferredLanguages: CoachDiscoveryForm["preferredLanguages"];
      coachGender: CoachDiscoveryForm["coachGender"];
      coachAges: CoachDiscoveryForm["coachAges"];
      coachingPackages: CoachDiscoveryForm["coachingPackages"];
      coachingApproaches: CoachDiscoveryForm["coachingApproaches"];
      coachPersonality: CoachDiscoveryForm["coachPersonality"];
      userPersonality: CoachDiscoveryForm["userPersonality"];
      faithIntegration: CoachDiscoveryForm["faithIntegration"];
      otherFaithIntegration: CoachDiscoveryForm["otherFaithIntegration"];
      personalContext: CoachDiscoveryForm["personalContext"];
      statusFilters: CoachDiscoveryForm["statusFilters"];
    };
  }, [watchedFields]);

  useEffect(() => {
    if (
      formValues.otherGoal !== undefined &&
      !formValues.goals.includes("Other")
    ) {
      setValue("otherGoal", undefined);
    }
  }, [formValues.goals, formValues.otherGoal, setValue]);

  useEffect(() => {
    if (
      formValues.otherFaithIntegration !== undefined &&
      !formValues.faithIntegration.includes("Other")
    ) {
      setValue("otherFaithIntegration", undefined);
    }
  }, [formValues.faithIntegration, formValues.otherFaithIntegration, setValue]);

  const [validatedPreferences, setValidatedPreferences] = useState<
    CoachDiscoveryForm | undefined
  >(undefined);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const validatedPreferencesRef = useRef<string | undefined>(undefined);

  type ValidationRunMode = "initial" | "change";

  const validateAndSyncPreferences = useCallback(
    async ({
      mode,
      isCancelled,
    }: {
      mode: ValidationRunMode;
      isCancelled: () => boolean;
    }): Promise<void> => {
      const isValid = await trigger(undefined, { shouldFocus: false });

      if (isCancelled()) {
        return;
      }

      setHasValidationErrors(!isValid);

      if (!isValid) {
        validatedPreferencesRef.current = undefined;
        setValidatedPreferences(undefined);
        return;
      }

      const latestValues = getValues();
      const serializedLatest = JSON.stringify(latestValues);

      if (
        mode === "change" &&
        serializedLatest === validatedPreferencesRef.current
      ) {
        return;
      }

      validatedPreferencesRef.current = serializedLatest;
      setValidatedPreferences(latestValues);
    },
    [getValues, trigger],
  );

  useEffect(() => {
    if (!initialFormData) {
      return;
    }

    // Sync form state with initial data to prevent incorrect updates.
    (Object.keys(initialFormData) as CoachDiscoveryField[]).forEach((key) => {
      const value = initialFormData[key];
      if (value !== undefined) {
        setValue(key, value);
      }
    });

    // URL presets take precedence over saved preferences.
    if (presetFormat) {
      setValue("coachingFormats", presetFormat);
    }

    let cancelled = false;

    void validateAndSyncPreferences({
      mode: "initial",
      isCancelled: () => cancelled,
    });

    return () => {
      cancelled = true;
    };
  }, [initialFormData, presetFormat, setValue, validateAndSyncPreferences]);

  const serializedFormValues = useMemo(
    () => JSON.stringify(formValues),
    [formValues],
  );

  // Debounce form changes, validate, and only submit valid preferences
  useEffect(() => {
    if (!formValues || Object.keys(formValues).length === 0) {
      return;
    }

    if (serializedFormValues === validatedPreferencesRef.current) {
      return;
    }

    let cancelled = false;

    const timeoutId = setTimeout(() => {
      void validateAndSyncPreferences({
        mode: "change",
        isCancelled: () => cancelled,
      });
    }, 700);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [serializedFormValues, validateAndSyncPreferences]);

  const preferencesForQuery = validatedPreferences;
  const shouldShowOtherGoal = formValues.goals.includes("Other");
  const shouldShowOtherFaith = formValues.faithIntegration.includes("Other");
  const canSavePreferences =
    !hasValidationErrors &&
    !isSavingPreferences &&
    validatedPreferencesRef.current !== undefined;

  const handleSavePreferences = useCallback(async (): Promise<void> => {
    if (isSavingPreferences) {
      return;
    }

    setIsSavingPreferences(true);
    try {
      const isValid = await trigger(undefined, { shouldFocus: true });
      setHasValidationErrors(!isValid);

      if (!isValid) {
        toast.error("Please resolve the highlighted fields before saving.");
        return;
      }

      const latestValues = getValues();
      const serializedLatest = JSON.stringify(latestValues);
      validatedPreferencesRef.current = serializedLatest;
      setValidatedPreferences(latestValues);

      await savePreferencesFn({ data: latestValues });

      await queryClient.invalidateQueries({
        queryKey: coachDiscoveryFormQuery.queryKey,
      });

      toast.success("Preferences saved.");
    } catch (error) {
      console.error("Failed to save preferences in coach discovery", {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString(),
        hasValidatedPreferences: validatedPreferencesRef.current !== undefined,
        context: "coach-discovery/handleSavePreferences",
      });

      toast.error("Failed to save preferences. Please try again.");
    } finally {
      setIsSavingPreferences(false);
    }
  }, [
    getValues,
    isSavingPreferences,
    queryClient,
    savePreferencesFn,
    setValidatedPreferences,
    trigger,
  ]);

  const renderSkeletonGrid = () => (
    <div className="grid grow-3 basis-0 grid-cols-1 items-start justify-stretch gap-4 md:grid-cols-2">
      {Array.from({ length: 8 }).map((_, i) => (
        <CoachCardSkeleton
          key={`skeleton-${i}`}
          className="w-full animate-in fade-in"
          seed={i}
        />
      ))}
    </div>
  );

  function CoachesSection({
    preferences,
  }: {
    preferences: Partial<CoachDiscoveryForm>;
  }) {
    const { data: coaches }: { data: CoachCardMinimalData[] } =
      useSuspenseQuery(coachesQuery(preferences));

    if (!coaches || coaches.length === 0) {
      return (
        <div className="grow-3 basis-0 pt-12">
          <EmptyState
            variant="hero"
            size="lg"
            icon={Search}
            title="No Dream Coach for you... yet"
            description="We looked hard but couldn't find a match. Try adjusting your filters and search again."
          />
        </div>
      );
    }

    return (
      <div className="grid grow-3 basis-0 grid-cols-1 items-start justify-stretch gap-4 md:grid-cols-2">
        {coaches.map((data, index) => (
          <CoachCard
            key={data.coach.id}
            coachData={data}
            source="coach_finder"
            className="animate-in fade-in"
            style={{ animationDelay: `${index * 50}ms` }}
          />
        ))}
      </div>
    );
  }

  class CoachesErrorBoundary extends React.Component<
    { children: React.ReactNode },
    { hasError: boolean; parsedError: ParsedError | null }
  > {
    constructor(props: { children: React.ReactNode }) {
      super(props);
      this.state = { hasError: false, parsedError: null };
    }
    static getDerivedStateFromError(error: unknown) {
      return { hasError: true, parsedError: parseServerError(error) };
    }
    render() {
      if (this.state.hasError && this.state.parsedError) {
        const { parsedError } = this.state;

        // Show validation errors (user errors - warning style)
        if (parsedError.type === "validation") {
          return (
            <div className="grow-3 basis-0 self-stretch pt-8">
              <div className="sticky top-[calc(var(--app-topbar-h)+1rem)] flex justify-center">
                <ValidationMessage
                  variant="warning"
                  title="Please check your filters"
                  message="Update the fields above to search for coaches."
                  fieldErrors={parsedError.fieldErrors}
                  fieldLabels={errorFieldLabels}
                  className="max-w-md"
                />
              </div>
            </div>
          );
        }

        // Show server error with image
        return (
          <div className="grow-3 basis-0 pt-12">
            <div className="flex flex-col items-center gap-4">
              <div className="aspect-square w-48 max-w-full">
                <img
                  src={ServerErrorGraphicUrl}
                  alt="Server error"
                  className="h-full w-full"
                />
              </div>
              <h1 className="text-center font-heading text-2xl font-medium tracking-tight">
                That didn't work
              </h1>
              <p className="text-text-secondary max-w-md text-center">
                {parsedError.message ||
                  "We're sorry, something went wrong finding coaches for you. Try refreshing the page or changing your filters."}
              </p>
            </div>
          </div>
        );
      }
      return this.props.children;
    }
  }

  return (
    <div className="relative flex w-full flex-col overflow-visible">
      <div className="mb-2 flex justify-between">
        <PageTitle>Find A Coach</PageTitle>
      </div>
      <div className="flex items-start justify-stretch gap-4">
        <Card className="h-fit w-100 max-w-full grow-1 basis-0 p-4 shadow-none hover:shadow-none">
          <div className="mb-6 flex items-center justify-between gap-3">
            <h3 className="font-heading text-2xl">Filters</h3>
            <Button
              type="button"
              size="sm"
              onClick={handleSavePreferences}
              disabled={!canSavePreferences}
            >
              {isSavingPreferences ? "Saving..." : "Save preferences"}
            </Button>
          </div>

          <form className="w-full space-y-8">
            {/* Coach Status Filters */}
            <FormField
              label="Coach status"
              error={getErrorMessage(formState.errors.statusFilters)}
            >
              <CheckboxGroupField
                name="statusFilters"
                control={control}
                options={coachStatusOptions}
              />
            </FormField>

            {/* Goals to Achieve */}
            <div className="mt-4 space-y-4">
              <FormField
                label="Goals"
                error={getErrorMessage(formState.errors.goals)}
              >
                <CheckboxGroupField
                  name="goals"
                  control={control}
                  options={goalsField.options || []}
                  maxSelections={2}
                />
              </FormField>
              <div
                className={cn(
                  "max-h-0 overflow-hidden px-1 opacity-0 transition-[max-height,opacity] duration-500",
                  {
                    "max-h-56 opacity-100": shouldShowOtherGoal,
                  },
                )}
              >
                <FormField
                  labelSize="sm"
                  label="Describe your goal"
                  error={
                    formState.touchedFields.otherGoal
                      ? getErrorMessage(formState.errors.otherGoal)
                      : undefined
                  }
                >
                  <Textarea
                    {...register("otherGoal")}
                    value={watch("otherGoal") || ""}
                    maxLength={otherGoalField.maxLength}
                    placeholder={otherGoalField.placeholder}
                    className="resize-none"
                  />
                </FormField>
              </div>
            </div>

            {/* Coaching Formats */}
            <FormField
              label={coachingFormatsFields.subTitle || ""}
              error={getErrorMessage(formState.errors.coachingFormats)}
            >
              <ControlledRadioGroup
                control={control}
                name="coachingFormats"
                options={
                  coachingFormatsFields.options?.map(({ value, label }) => ({
                    value,
                    label,
                  })) || []
                }
              />
            </FormField>

            {/* Session Delivery Methods */}
            <FormField
              label={sessionDeliveryMethodsFeild.subTitle || ""}
              error={getErrorMessage(formState.errors.sessionDeliveryMethods)}
            >
              <ControlledRadioGroup
                control={control}
                name="sessionDeliveryMethods"
                options={
                  sessionDeliveryMethodsFeild.options?.map(
                    ({ value, label }) => ({
                      value,
                      label,
                    }),
                  ) || []
                }
              />
            </FormField>

            {/* Location */}
            <FormField
              label={prefersCoachLocationField.subTitle || ""}
              error={getErrorMessage(formState.errors.prefersCoachLocation)}
            >
              <Controller
                control={control}
                name="prefersCoachLocation"
                render={({ field }) => (
                  <RadioGroup
                    value={field.value ?? ""}
                    onValueChange={field.onChange}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        value={
                          prefersCoachLocationField?.options?.[0]?.value || ""
                        }
                        id={`prefersCoachLocation-${prefersCoachLocationField?.options?.[0]?.value}`}
                      />
                      <label
                        htmlFor={`prefersCoachLocation-${prefersCoachLocationField?.options?.[0]?.value}`}
                        className="cursor-pointer text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {prefersCoachLocationField?.options?.[0]?.label || ""}
                      </label>
                    </div>
                    {/* Conditional Location Details */}
                    <div
                      className={cn(
                        "max-h-0 overflow-hidden opacity-0 transition-[max-height,opacity] duration-700",
                        {
                          "max-h-screen opacity-100": field.value === "Yes",
                        },
                      )}
                    >
                      <div className="mt-4 flex flex-col gap-4 px-1">
                        <FormField
                          label={coachLocationCountryField.subTitle}
                          error={getErrorMessage(
                            formState.errors.coachLocationCountry,
                          )}
                        >
                          <Controller
                            control={control}
                            name="coachLocationCountry"
                            render={({ field: countryField }) => (
                              <CountrySelect
                                value={countryField.value}
                                onChange={countryField.onChange}
                              />
                            )}
                          />
                        </FormField>
                        <FormField
                          label={coachLocationRegionField.subTitle || ""}
                          error={getErrorMessage(
                            formState.errors.coachLocationRegion,
                          )}
                        >
                          <Input
                            {...register("coachLocationRegion")}
                            error={getErrorMessage(
                              formState.errors.coachLocationRegion,
                            )}
                          />
                        </FormField>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        value={
                          prefersCoachLocationField?.options?.[1]?.value || ""
                        }
                        id={`prefersCoachLocation-${prefersCoachLocationField?.options?.[1]?.value}`}
                      />
                      <label
                        htmlFor={`prefersCoachLocation-${prefersCoachLocationField?.options?.[1]?.value}`}
                        className="cursor-pointer text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {prefersCoachLocationField?.options?.[1]?.label ?? ""}
                      </label>
                    </div>
                  </RadioGroup>
                )}
              />
            </FormField>

            {/* Preferred Languages */}
            <FormField
              label="Language"
              error={getErrorMessage(formState.errors.preferredLanguages)}
            >
              <LanguageSelect
                field={{
                  value: getValues("preferredLanguages"),
                  name: "preferredLanguages",
                }}
                props={register("preferredLanguages")}
                variant="slim"
                placeholder=""
              />
            </FormField>

            {/* Coach Gender */}
            <FormField
              label="Coach gender"
              error={getErrorMessage(formState.errors.coachGender)}
            >
              <ControlledRadioGroup
                control={control}
                name="coachGender"
                options={
                  coachGenderField.options?.map(({ value, label }) => ({
                    value,
                    label,
                  })) || []
                }
              />
            </FormField>

            {/* Coach Age */}
            <FormField
              label="Coach ages"
              error={getErrorMessage(formState.errors.coachAges)}
            >
              <CustomMultiSelect
                field={{
                  value: getValues("coachAges") || [],
                  name: "coachAges",
                }}
                props={register("coachAges")}
                placeholder=""
                variant="slim"
                items={coachAgesField.options || []}
                exclusiveValues={["No preference"]}
              />
            </FormField>

            <Button
              type="button"
              onClick={() => setIsAdvancedExpanded(!isAdvancedExpanded)}
            >
              {isAdvancedExpanded ? (
                <>
                  Hide advanced filters <ChevronUp className="size-[1em]" />
                </>
              ) : (
                <>
                  Show advanced filters <ChevronDown className="size-[1em]" />
                </>
              )}
            </Button>

            <div
              className={cn(
                "flex flex-col items-stretch space-y-8 opacity-0 transition-opacity",
                {
                  "opacity-100": isAdvancedExpanded,
                },
              )}
            >
              {/* Start Time */}
              <FormField
                label={startTimeField.subTitle || ""}
                error={getErrorMessage(formState.errors.startTime)}
              >
                <Controller
                  control={control}
                  name="startTime"
                  render={({ field }) => (
                    <RadioGroup
                      value={field.value ?? ""}
                      onValueChange={field.onChange}
                      className="flex flex-col gap-y-4"
                    >
                      {startTimeField.options?.map(({ value, label }) => (
                        <div
                          key={value}
                          className="flex items-center space-x-2"
                        >
                          <RadioGroupItem
                            value={value}
                            id={`startTime-${value}`}
                          />
                          <label
                            htmlFor={`startTime-${value}`}
                            className="cursor-pointer text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            {label}
                          </label>
                        </div>
                      )) || null}
                    </RadioGroup>
                  )}
                />
              </FormField>

              {/* Support */}
              <FormField
                label={supportField.subTitle || ""}
                error={getErrorMessage(formState.errors.support)}
              >
                <Controller
                  control={control}
                  name="support"
                  render={({ field }) => (
                    <RadioGroup
                      value={field.value ?? ""}
                      onValueChange={field.onChange}
                      className="flex flex-col gap-y-4"
                    >
                      {supportField.options?.map(
                        ({ value, label, description }) => (
                          <div key={value} className="flex items-start gap-2">
                            <RadioGroupItem
                              value={value}
                              id={`support-${value}`}
                            />
                            <div className="grid gap-1.5 leading-none">
                              <label
                                htmlFor={`support-${value}`}
                                className="cursor-pointer text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                              >
                                {label}
                              </label>
                              {description ? (
                                <p className="text-sm text-muted-foreground">
                                  {description}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        ),
                      ) || null}
                    </RadioGroup>
                  )}
                />
              </FormField>

              {/* Coaching Experience Level */}
              <FormField
                label="Coaching experience levels"
                error={getErrorMessage(formState.errors.coachingPackages)}
              >
                <CustomMultiSelect
                  field={{
                    value: getValues("coachingPackages") || [],
                    name: "coachingPackages",
                  }}
                  props={register("coachingPackages")}
                  placeholder=""
                  variant="slim"
                  items={coachingPackagesField.options || []}
                  exclusiveValues={["No preference"]}
                />
              </FormField>

              {/* Coaching Style */}
              <FormField
                label={"Coaching Style"}
                error={getErrorMessage(formState.errors.coachingApproaches)}
              >
                <CustomMultiSelect
                  field={{
                    value: getValues("coachingApproaches") || [],
                    name: "coachingApproaches",
                  }}
                  props={register("coachingApproaches")}
                  placeholder=""
                  variant="slim"
                  exclusiveValues={["Not sure yet"]}
                  items={coachingApproachesField.options || []}
                />
              </FormField>

              {/* Coach Personality */}
              <div className="mt-8">
                <FormField
                  labelSize="sm"
                  error={getErrorMessage(formState.errors.coachPersonality)}
                  label="Preferred coach personality traits"
                >
                  <CheckboxGroupField
                    name="coachPersonality"
                    control={control}
                    options={coachPersonalityField.options || []}
                    exclusiveOptions={["Skip this question"]}
                    maxSelections={2}
                  />
                </FormField>
              </div>

              {/* User Personality */}
              <FormField
                labelSize="sm"
                error={getErrorMessage(formState.errors.userPersonality)}
                label={userPersonalityField.subTitle}
              >
                <CheckboxGroupField
                  name="userPersonality"
                  control={control}
                  options={userPersonalityField.options || []}
                  exclusiveOptions={["Skip this question"]}
                  maxSelections={2}
                />
              </FormField>

              {/* Faith Integration */}
              <FormField
                label="Faith preferences"
                error={getErrorMessage(formState.errors.faithIntegration)}
              >
                <CheckboxGroupField
                  name="faithIntegration"
                  control={control}
                  options={faithIntegrationsField.options || []}
                  exclusiveOptions={["No preference"]}
                  onTrigger={{
                    "No preference": () =>
                      setValue("otherFaithIntegration", undefined),
                  }}
                />
              </FormField>

              <div
                className={cn(
                  "max-h-0 overflow-hidden px-1 opacity-0 transition-[max-height,opacity] duration-500",
                  {
                    "max-h-56 opacity-100": shouldShowOtherFaith,
                  },
                )}
              >
                <FormField
                  labelSize="sm"
                  label="Share your faith preferences"
                  error={getErrorMessage(
                    formState.errors.otherFaithIntegration,
                  )}
                >
                  <Textarea
                    {...register("otherFaithIntegration")}
                    value={watch("otherFaithIntegration") || ""}
                    maxLength={otherFaithIntegrationField.maxLength}
                    placeholder={otherFaithIntegrationField.placeholder}
                    className="resize-none"
                  />
                </FormField>
              </div>

              {/* Personal Context */}
              <FormField
                label="Personal context"
                error={getErrorMessage(formState.errors.personalContext)}
              >
                <Textarea
                  {...register("personalContext")}
                  value={watch("personalContext") || ""}
                  maxLength={personalContextField.maxLength}
                  placeholder={personalContextField.placeholder}
                  className="resize-none"
                />
              </FormField>
            </div>
          </form>
        </Card>

        {hasValidationErrors ? (
          <div className="grow-3 basis-0 self-stretch pt-8">
            <div className="sticky top-[calc(var(--app-topbar-h)+1rem)] flex justify-center">
              <ValidationMessage
                variant="warning"
                title="Update your filters to see coach matches"
                message="Please resolve the highlighted fields above before we search for new coaches."
                fieldErrors={formErrorsToFieldErrors(formState.errors)}
                fieldLabels={errorFieldLabels}
                className="max-w-md"
              />
            </div>
          </div>
        ) : preferencesForQuery ? (
          <React.Suspense fallback={renderSkeletonGrid()}>
            <CoachesErrorBoundary>
              <CoachesSection preferences={preferencesForQuery} />
            </CoachesErrorBoundary>
          </React.Suspense>
        ) : (
          renderSkeletonGrid()
        )}
      </div>
    </div>
  );
}
